import { useEffect, useState } from 'react';

// ECOMM-10367: the kiosk creator secret. createPublicContextSession returns it once;
// the creating display stores it here (sessionStorage, NOT the URL — the session id
// already travels in the URL, and the secret must not share that exposure) and presents
// it back on getPublicContextSession to retrieve the JWT. Keyed by session id so a new
// session can't read a stale secret.
const SECRET_KEY_PREFIX = 'kiosk-public-session-secret:';

const secretKey = (sessionId: string): string => `${SECRET_KEY_PREFIX}${sessionId}`;

export function storePublicSessionSecret(sessionId: string, secret: string | null | undefined): void {
  if (typeof window === 'undefined' || !sessionId || !secret) {
    return;
  }
  try {
    window.sessionStorage.setItem(secretKey(sessionId), secret);
  } catch {
    // sessionStorage can throw in private-mode/quota edge cases; a missing secret
    // simply means the display won't receive a token (fails closed), never a crash.
  }
}

// Remove a single session's secret once it has served its purpose (i.e. right after the
// display logs in with it). Minimizes how long the credential lingers in sessionStorage.
export function removePublicSessionSecret(sessionId: string): void {
  if (typeof window === 'undefined' || !sessionId) {
    return;
  }
  try {
    window.sessionStorage.removeItem(secretKey(sessionId));
  } catch {
    // no-op: clearing is best-effort hygiene, never worth throwing over.
  }
}

// Sweep every kiosk creator secret. Called when a new customer starts at the lock screen,
// so abandoned/never-completed sessions don't leave secrets piling up on a long-lived
// kiosk tab. Only touches our own prefixed keys, never other sessionStorage entries.
export function clearAllPublicSessionSecrets(): void {
  if (typeof window === 'undefined') {
    return;
  }
  try {
    const keys: string[] = [];
    for (let i = 0; i < window.sessionStorage.length; i += 1) {
      const key = window.sessionStorage.key(i);
      if (key?.startsWith(SECRET_KEY_PREFIX)) {
        keys.push(key);
      }
    }
    keys.forEach((key) => window.sessionStorage.removeItem(key));
  } catch {
    // no-op: best-effort cleanup.
  }
}

export function useGetSessionSecret(sessionId: string): string | undefined {
  // Read post-mount to stay SSR-safe and avoid hydration mismatch. The secret is written
  // by the lock screen before the client-side navigation here, so it's present on mount.
  // Returns undefined (not '') when absent so Apollo omits the `secret` variable entirely
  // rather than sending an empty `?secret=` on the @rest GET — a cleaner signal to the backend.
  const [secret, setSecret] = useState<string | undefined>(undefined);

  useEffect(() => {
    if (typeof window === 'undefined' || !sessionId) {
      return;
    }
    try {
      setSecret(window.sessionStorage.getItem(secretKey(sessionId)) ?? undefined);
    } catch {
      setSecret(undefined);
    }
  }, [sessionId]);

  return secret;
}
